import { todayStr, toLocalDateStr } from "../lib/date";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { useAuth } from "../App";
import MasterScheduleTab from "../components/MasterScheduleTab";
import {
  getCalendarEvents, createCalendarEvent, updateCalendarEvent, deleteCalendarEvent,
  getRoster, getStudents, getStaff,
  getSchoolCalendars, createSchoolCalendar, updateSpecialDates,
} from "../lib/api";
import { printPage } from "../lib/print";
import { resolveLegend, LEGEND_HEX } from "../lib/calendarLegend";
import { SchoolCalendarTab } from "../components/calendar/SchoolCalendarTab";
import { colorForStaff } from "../lib/staffColors";
import { SchoolYearCalendarTab } from "../components/calendar/SchoolYearCalendarTab";
import { SchoolClosuresSection } from "../components/calendar/SchoolOpsSection";

export default function CalendarPage() {
  const { isAdmin, hasPermission, user } = useAuth();
  const canEditCalendar = isAdmin() || hasPermission("add_calendar_events");
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "master-schedule";
  const setActiveTab = (v) => {
    const next = new URLSearchParams(searchParams);
    if (v === "master-schedule") next.delete("tab");
    else next.set("tab", v);
    setSearchParams(next, { replace: true });
  };
  const openTodayView = searchParams.get("today") === "true";
  const [rawEvents, setRawEvents] = useState([]);
  const [roster, setRoster] = useState(null);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEventId, setEditingEventId] = useState(null);
  const [savingEvent, setSavingEvent] = useState(false);
  const [viewMode, setViewMode] = useState("full");

  // New Event state
  const [newEvent, setNewEvent] = useState({
    title: "", date: todayStr(), end_date: "",
    start_time: "", end_time: "", type: "event", is_half_day: false,
    is_public: false, notes: "", notify_staff: [],
  });
  const [staff, setStaff] = useState([]);
  const [selectAllStaff, setSelectAllStaff] = useState(false);
  // Meetings & Events staff filter: "all" (whole team) or "mine" (created by / assigned to me)
  const [staffFilter, setStaffFilter] = useState("all");

  // School Year Calendar state
  const [schoolCalendar, setSchoolCalendar] = useState(null);
  const [allCalendars, setAllCalendars] = useState([]);
  const [selectedCalendarYear, setSelectedCalendarYear] = useState("");
  const [calendarDialogOpen, setCalendarDialogOpen] = useState(false);
  const [addDateDialogOpen, setAddDateDialogOpen] = useState(false);
  const [calendarFormData, setCalendarFormData] = useState({
    academic_year: "2025-2026", school_name: "", first_day: "", last_day: "",
  });
  const [newSpecialDate, setNewSpecialDate] = useState({
    date: "", end_date: "", type: "holiday", name: "", recurring_day: "",
  });

  // ── Data Fetching ──
  // Cache staff (and their color assignments) once on app load. Staff colors do not
  // change as the user navigates between months, so we never re-fetch them per render.
  useEffect(() => {
    getStaff().then((r) => setStaff([...(r.data || [])].sort((a, b) => (a.name || "").localeCompare(b.name || "")))).catch(() => { /* non-fatal */ });
  }, []);

  // Fetch ONLY the events within the currently visible date range (a padded window
  // around the selected month — covers the widest view, the 6-week month grid), not
  // the whole year. Navigation changes `selectedDate`, which re-runs this fetch.
  const fetchEvents = async () => {
    setLoading(true);
    try {
      const y = selectedDate.getFullYear();
      const mo = selectedDate.getMonth();
      const winStart = new Date(y, mo, 1); winStart.setDate(winStart.getDate() - 10);
      const winEnd = new Date(y, mo + 1, 0); winEnd.setDate(winEnd.getDate() + 20);
      const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      const [eventsRes, rosterRes, studentsRes, calendarsRes] = await Promise.all([
        getCalendarEvents({ start_date: fmt(winStart), end_date: fmt(winEnd), is_public: viewMode === "public" ? true : undefined }),
        getRoster(toLocalDateStr(selectedDate)),
        getStudents("current"), getSchoolCalendars(),
      ]);
      setRoster(rosterRes.data);
      setStudents(studentsRes.data);
      setRawEvents(eventsRes.data);

      if (calendarsRes.data.length > 0) {
        setAllCalendars(calendarsRes.data);
        // Default calendar: the one covering today; else the next upcoming (so once
        // the last day of school passes — even during summer — the next year's
        // calendar shows automatically); else the most recent past one.
        const today = todayStr();
        const cals = calendarsRes.data;
        let smartCal = cals.find(c => c.first_day && c.last_day && c.first_day <= today && today <= c.last_day);
        if (!smartCal) {
          const upcoming = cals.filter(c => c.first_day && c.first_day > today).sort((a, b) => a.first_day.localeCompare(b.first_day));
          smartCal = upcoming[0];
        }
        if (!smartCal) {
          const past = [...cals].filter(c => c.last_day).sort((a, b) => b.last_day.localeCompare(a.last_day));
          smartCal = past[0] || cals[0];
        }
        // Honor an explicit user selection; otherwise use the smart pick.
        const targetCal = (selectedCalendarYear && cals.find(c => c.academic_year === selectedCalendarYear)) || smartCal;
        setSchoolCalendar(targetCal);
        setSelectedCalendarYear(targetCal.academic_year);
        setCalendarFormData({
          academic_year: targetCal.academic_year, school_name: targetCal.school_name,
          first_day: targetCal.first_day, last_day: targetCal.last_day,
          notice: targetCal.notice || "", legend_labels: targetCal.legend_labels || {},
        });
      } else {
        setAllCalendars([]);
      }
    } catch { toast.error("Failed to load calendar data"); } finally { setLoading(false); }
  };

  useEffect(() => { fetchEvents(); }, [selectedDate, viewMode, selectedCalendarYear]);

  // Combine backend events (visible range) with client-generated birthday and
  // school-calendar events. Derived via useMemo so the frame can render immediately
  // and re-populate as staff / students / calendars finish loading.
  const events = useMemo(() => {
    const year = selectedDate.getFullYear();
    const isPublicView = viewMode === "public";

    // Only include current students/staff for birthdays
    const currentStudents = students.filter(s => s.status === "current" || !s.status);
    const activeStaff = staff.filter(s => s.status !== "former" && s.status !== "deleted");

    // For public view, only show staff birthdays (not students)
    const birthdayPeople = isPublicView
      ? activeStaff.map(s => ({ ...s, _isStaff: true }))
      : [
          ...currentStudents.map(s => ({ ...s, _isStaff: false })),
          ...activeStaff.map(s => ({ ...s, _isStaff: true }))
        ];

    const birthdayEvents = [];
    birthdayPeople.forEach(person => {
      const dob = person.dob || person.date_of_birth;
      if (!dob) return;
      const dobDate = new Date(dob + 'T00:00:00');
      if (isNaN(dobDate.getTime())) return;
      const name = person.first_name ? `${person.preferred_name || person.first_name} ${person.last_name}` : person.name;
      birthdayEvents.push({
        id: `bday_${person.id}_${year}`, title: `${name}'s Birthday`,
        date: `${year}-${String(dobDate.getMonth() + 1).padStart(2, '0')}-${String(dobDate.getDate()).padStart(2, '0')}`,
        type: "birthday", is_birthday: true, is_staff: person._isStaff,
      });
    });

    // School-year calendar special dates (holidays, breaks, conferences, first/last
    // day) also surface as READ-ONLY events on the Meetings & Events calendar so they
    // show up when people browse it. Recurring default-week fillers (auto_generated,
    // e.g. "every Friday early release") are skipped to avoid clutter.
    const SC_TYPE_MAP = {
      no_school: "holiday", vacation: "holiday", holiday: "holiday",
      conference: "meeting", teacher_inservice: "event", early_release: "event", event: "event",
    };
    const yStart = `${year}-01-01`, yEnd = `${year}-12-31`;
    const schoolCalEvents = [];
    const _seenSc = new Set();
    (allCalendars || []).forEach((cal) => {
      (cal.special_dates || []).forEach((sd, i) => {
        if (!sd.date || sd.date < yStart || sd.date > yEnd || sd.auto_generated) return;
        const type = SC_TYPE_MAP[sd.type] || "other";
        const key = `${sd.date}|${type}|${sd.name || ""}`;
        if (_seenSc.has(key)) return;
        _seenSc.add(key);
        schoolCalEvents.push({
          id: `sc_${cal.academic_year}_${sd.type}_${sd.date}_${i}`,
          title: sd.name || "School Calendar", date: sd.date,
          type,
          is_public: true, read_only: true, source: "school_calendar",
        });
      });
      [["first_day", "First Day of School"], ["last_day", "Last Day of School"]].forEach(([k, label]) => {
        const d = cal[k];
        if (d && d >= yStart && d <= yEnd && !_seenSc.has(`${d}|event|${label}`)) {
          _seenSc.add(`${d}|event|${label}`);
          schoolCalEvents.push({ id: `sc_${cal.academic_year}_${k}`, title: label, date: d, type: "event", is_public: true, read_only: true, source: "school_calendar" });
        }
      });
    });

    return [...rawEvents, ...birthdayEvents, ...schoolCalEvents];
  }, [rawEvents, students, staff, allCalendars, viewMode, selectedDate]);

  // ── Event Handlers ──
  const resetEventForm = () => {
    setNewEvent({ title: "", date: todayStr(), end_date: "", start_time: "", end_time: "", type: "event", is_half_day: false, is_public: false, notes: "", notify_staff: [] });
    setSelectAllStaff(false);
    setEditingEventId(null);
    setSavingEvent(false);
  };

  const openAddEvent = () => { resetEventForm(); setDialogOpen(true); };

  const openAddEventForDate = (dateStr) => {
    setNewEvent({ title: "", date: dateStr, end_date: "", start_time: "", end_time: "", type: "event", is_half_day: false, is_public: false, notes: "", notify_staff: [] });
    setSelectAllStaff(false);
    setEditingEventId(null);
    setDialogOpen(true);
  };

  const openEditEvent = (event) => {
    setEditingEventId(event.id);
    const assignees = event.notify_staff || [];
    setNewEvent({
      title: event.title || "", date: event.date || todayStr(),
      end_date: event.end_date || "", start_time: event.start_time || "", end_time: event.end_time || "",
      type: event.type || "event", is_half_day: !!event.is_half_day, is_public: event.is_public !== false,
      notes: event.notes || "", notify_staff: assignees,
    });
    setSelectAllStaff(staff.length > 0 && assignees.length === staff.length);
    setDialogOpen(true);
  };

  const handleSaveEvent = async (e) => {
    e.preventDefault();
    if (!newEvent.title || !newEvent.date) { toast.error("Please fill in required fields"); return; }
    if (savingEvent) return;
    setSavingEvent(true);
    try {
      if (editingEventId) {
        await updateCalendarEvent(editingEventId, newEvent);
        toast.success("Event updated", { position: "bottom-right" });
        if (newEvent.notify_staff.length > 0) toast.success(`Notifications sent to ${newEvent.notify_staff.length} staff members`, { position: "bottom-right" });
      } else {
        await createCalendarEvent(newEvent);
        toast.success("Event created", { position: "bottom-right" });
        if (newEvent.notify_staff.length > 0) toast.success(`Notifications sent to ${newEvent.notify_staff.length} staff members`, { position: "bottom-right" });
      }
      setDialogOpen(false);
      resetEventForm();
      fetchEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save event");
      setSavingEvent(false); // re-enable only on failure so staff can retry
    }
  };

  const handleStaffNotifyToggle = (staffId) => {
    setNewEvent(prev => {
      const current = prev.notify_staff || [];
      const updated = current.includes(staffId) ? current.filter(id => id !== staffId) : [...current, staffId];
      setSelectAllStaff(updated.length === staff.length);
      return { ...prev, notify_staff: updated };
    });
  };

  const handleSelectAllStaff = () => {
    if (selectAllStaff) { setNewEvent(prev => ({ ...prev, notify_staff: [] })); setSelectAllStaff(false); }
    else { setNewEvent(prev => ({ ...prev, notify_staff: staff.map(s => s.id) })); setSelectAllStaff(true); }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm("Delete this event?")) return;
    try { await deleteCalendarEvent(eventId); toast.success("Event deleted"); fetchEvents(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed to delete event"); }
  };

  // ── School Year Calendar Handlers ──
  const handleCreateCalendar = async () => {
    try {
      await createSchoolCalendar({ ...calendarFormData, special_dates: schoolCalendar?.special_dates || [] });
      toast.success("School calendar saved"); setCalendarDialogOpen(false); fetchEvents();
    } catch { toast.error("Failed to save calendar"); }
  };

  const handleAddSpecialDate = async () => {
    if (!newSpecialDate.name) { toast.error("Please fill in the name"); return; }
    const updatedDates = [...(schoolCalendar?.special_dates || [])];
    if (newSpecialDate.recurring_day) {
      const targetDay = parseInt(newSpecialDate.recurring_day);
      const firstDay = new Date(schoolCalendar.first_day + 'T00:00:00');
      const lastDay = new Date(schoolCalendar.last_day + 'T00:00:00');
      let current = new Date(firstDay);
      while (current.getDay() !== targetDay) current.setDate(current.getDate() + 1);
      let addedCount = 0;
      while (current <= lastDay) {
        const dateStr = toLocalDateStr(current);
        if (!updatedDates.find(d => d.date === dateStr)) {
          updatedDates.push({ date: dateStr, type: newSpecialDate.type, name: newSpecialDate.name });
          addedCount++;
        }
        current.setDate(current.getDate() + 7);
      }
      if (addedCount === 0) { toast.error("No new dates to add"); return; }
    } else if (!newSpecialDate.date) { toast.error("Please select a date"); return; }
    else if (newSpecialDate.end_date && newSpecialDate.end_date > newSpecialDate.date) {
      let current = new Date(newSpecialDate.date + 'T00:00:00');
      const end = new Date(newSpecialDate.end_date + 'T00:00:00');
      while (current <= end) { updatedDates.push({ date: toLocalDateStr(current), type: newSpecialDate.type, name: newSpecialDate.name }); current.setDate(current.getDate() + 1); }
    } else { updatedDates.push({ date: newSpecialDate.date, type: newSpecialDate.type, name: newSpecialDate.name }); }
    try {
      await updateSpecialDates(schoolCalendar.academic_year, updatedDates);
      toast.success(newSpecialDate.recurring_day ? "Recurring dates added" : "Date added");
      setAddDateDialogOpen(false); setNewSpecialDate({ date: "", end_date: "", type: "holiday", name: "", recurring_day: "" }); fetchEvents();
    } catch { toast.error("Failed to add date"); }
  };

  const handleDeleteSpecialDate = async (dateToDelete) => {
    const updatedDates = (schoolCalendar?.special_dates || []).filter(d => d.date !== dateToDelete.date || d.name !== dateToDelete.name);
    try { await updateSpecialDates(schoolCalendar.academic_year, updatedDates); toast.success("Date removed"); fetchEvents(); }
    catch { toast.error("Failed to remove date"); }
  };

  // Delete a whole Key Date (a break/holiday can span multiple day entries with the same name+type)
  const handleDeleteKeyDate = async (name, type) => {
    const updatedDates = (schoolCalendar?.special_dates || []).filter(d => !(d.name === name && d.type === type));
    try { await updateSpecialDates(schoolCalendar.academic_year, updatedDates); toast.success(`"${name}" removed`); fetchEvents(); }
    catch { toast.error("Failed to remove"); }
  };

  // ── Utility Functions ──
  const countSchoolDays = (year, month) => {
    if (!schoolCalendar) return 0;
    const lastDay = new Date(year, month + 1, 0);
    const firstSchoolDate = new Date(schoolCalendar.first_day + 'T00:00:00');
    const lastSchoolDate = new Date(schoolCalendar.last_day + 'T00:00:00');
    let count = 0;
    for (let d = 1; d <= lastDay.getDate(); d++) {
      const date = new Date(year, month, d);
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      if (date.getDay() === 0 || date.getDay() === 6) continue;
      if (date < firstSchoolDate || date > lastSchoolDate) continue;
      const sd = schoolCalendar.special_dates?.find(s => s.date === dateStr);
      if (sd && (sd.type === 'holiday' || sd.type === 'no_school')) continue;
      count++;
    }
    return count;
  };

  const handlePrintCalendar = () => {
    if (!schoolCalendar) return;
    const startYear = parseInt(schoolCalendar.academic_year.split('-')[0]);
    const legend = resolveLegend(schoolCalendar);
    const firstLastHex = LEGEND_HEX.event;
    const colorForType = (type) => LEGEND_HEX[type] || "#e5e7eb";
    const genMonth = (year, month) => {
      const f = new Date(year, month, 1); const l = new Date(year, month + 1, 0);
      const sd = f.getDay(); const dim = l.getDate();
      const mn = f.toLocaleDateString('en-US', { month: 'long' }); const sdays = countSchoolDays(year, month);
      let html = '';
      ['Su','Mo','Tu','We','Th','Fr','Sa'].forEach(d => { html += `<div class="day-header">${d}</div>`; });
      for (let i = 0; i < sd; i++) html += '<div class="day"></div>';
      for (let d = 1; d <= dim; d++) {
        const ds = `${year}-${String(month+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
        const sp = schoolCalendar.special_dates?.find(s => s.date === ds);
        const dow = new Date(year, month, d).getDay(); const we = dow === 0 || dow === 6;
        let style = '';
        if (ds === schoolCalendar.first_day || ds === schoolCalendar.last_day) style = `background-color:${firstLastHex}!important;font-weight:bold`;
        else if (sp) style = `background-color:${colorForType(sp.type)}!important`;
        else if (we) style = 'color:#bbb';
        html += `<div class="day" style="${style}">${d}</div>`;
      }
      return `<div class="month"><div class="month-header">${mn} (${sdays} days)</div><div class="days-grid">${html}</div></div>`;
    };
    // Legend chips — every configured type, with per-school custom labels.
    const legendHtml = legend.map(t => `<div class="legend-item"><div class="legend-color" style="background-color:${LEGEND_HEX[t.value]}!important"></div>${t.label}</div>`).join('');
    // Key dates (holidays / breaks / no-school), collapsing multi-day ranges.
    const uniqueEvents = [...new Map((schoolCalendar.special_dates || []).filter(d => d.type === 'holiday' || d.type === 'no_school' || d.type === 'vacation').map(d => [`${d.name}-${d.type}`, d])).values()].sort((a, b) => a.date.localeCompare(b.date));
    let eventsHtml = '';
    uniqueEvents.forEach(ev => {
      const same = schoolCalendar.special_dates.filter(d => d.name === ev.name && d.type === ev.type);
      const s = same[0]?.date; const e = same.length > 1 ? same[same.length - 1]?.date : null;
      const fmt = (ds) => new Date(ds + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      eventsHtml += `<div class="event"><span class="event-name">${ev.name}</span> ${e ? `${fmt(s)} – ${fmt(e)}` : fmt(s)}</div>`;
    });
    const noticeText = (schoolCalendar.notice || "").trim();
    const noticeHtml = noticeText ? `<div class="notice">${noticeText.replace(/</g, '&lt;')}</div>` : '';
    const calContent = `
      ${noticeHtml}
      <div class="top-row">
        <div class="legend">${legendHtml}</div>
      </div>
      <div class="calendar-grid">
        ${genMonth(startYear,7)}${genMonth(startYear,8)}${genMonth(startYear,9)}${genMonth(startYear,10)}
        ${genMonth(startYear,11)}${genMonth(startYear+1,0)}${genMonth(startYear+1,1)}${genMonth(startYear+1,2)}
        ${genMonth(startYear+1,3)}${genMonth(startYear+1,4)}${genMonth(startYear+1,5)}
      </div>
      <div class="events-section"><div class="events-title">Key Dates</div><div class="events-grid">${eventsHtml}</div></div>
    `;
    printPage({
      title: `${schoolCalendar.academic_year} ${schoolCalendar.school_name} Calendar`,
      subtitle: "",
      content: calContent,
      landscape: true,
      showFooter: false,
      printDelay: 300,
      extraStyles: `
        @page { size: landscape; margin: 0.4cm; }
        body { font-size: 10px; padding: 6px; }
        .print-header { border-bottom: none; padding-bottom: 2px; margin-bottom: 4px; }
        .print-header h1 { font-size: 17px; }
        .print-timestamp { display: none; }
        .notice { border: 1px solid #f59e0b; background-color:#fffbeb!important; color:#78350f; padding:5px 8px; border-radius:4px; font-size:10px; margin-bottom:6px; }
        .top-row { margin-bottom:6px; }
        .legend { display:flex; flex-wrap:wrap; gap:10px; font-size:9px; }
        .legend-item { display:flex; align-items:center; gap:4px; }
        .legend-color { width:14px; height:10px; border:1px solid #666!important; }
        .calendar-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:6px; }
        .month { border:1px solid #999!important; padding:3px; break-inside:avoid; }
        .month-header { font-weight:bold; text-align:center; font-size:10px; margin-bottom:3px; background-color:#eef2f7!important; padding:2px; }
        .days-grid { display:grid; grid-template-columns:repeat(7,1fr); gap:1px; }
        .day-header { text-align:center; font-weight:bold; font-size:7px; padding:1px; background-color:#e5e5e5!important; }
        .day { text-align:center; padding:2px 1px; font-size:8px; min-height:13px; border-radius:2px; }
        .events-section { margin-top:8px; border-top:1px solid #999!important; padding-top:6px; }
        .events-title { font-weight:bold; margin-bottom:4px; font-size:10px; }
        .events-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:2px 14px; font-size:9px; }
        .event-name { font-weight:600; }
      `,
    });
  };

  const generateSchoolYearMonths = () => {
    if (!schoolCalendar) return [];
    const startYear = parseInt(schoolCalendar.academic_year.split('-')[0]);
    const months = [];
    for (let m = 7; m <= 11; m++) months.push({ year: startYear, month: m });
    for (let m = 0; m <= 5; m++) months.push({ year: startYear + 1, month: m });
    return months;
  };

  const getSpecialDateInfo = (dateStr) => schoolCalendar?.special_dates?.find(d => d.date === dateStr) || null;

  const getEventsForDate = (date) => {
    const dateStr = toLocalDateStr(date);
    return events.filter(e => e.end_date ? (dateStr >= e.date && dateStr <= e.end_date) : e.date === dateStr);
  };

  const getEventTypeColor = (type) => ({
    holiday: "bg-red-100 text-red-800 border-red-200", snow_day: "bg-blue-100 text-blue-800 border-blue-200",
    meeting: "bg-purple-100 text-purple-800 border-purple-200", event: "bg-green-100 text-green-800 border-green-200",
    themed_week: "bg-amber-100 text-amber-800 border-amber-200", tour: "bg-cyan-100 text-cyan-800 border-cyan-200",
    staff_pto: "bg-teal-100 text-teal-800 border-teal-200",
    birthday: "bg-pink-100 text-pink-800 border-pink-200",
  }[type] || "bg-gray-100 text-gray-800 border-gray-200");

  const selectedDateEvents = getEventsForDate(selectedDate);

  // Text search across ALL events (any date) — used by the calendar search box to
  // locate a specific meeting (e.g. an IEP) that may be months away.
  const searchEvents = async (q) => {
    const res = await getCalendarEvents({ q, is_public: viewMode === "public" ? true : undefined });
    return res.data;
  };

  const getStaffColor = (s) => colorForStaff(s);
  const staffById = Object.fromEntries(staff.map((s) => [s.id, s]));
  const activeStaffIds = staff.filter((s) => s.status !== "former" && s.status !== "deleted").map((s) => s.id);

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#00305E] tracking-tight">Schedules & Calendar</h1>
          <p className="text-muted-foreground mt-1">View and manage school schedules</p>
        </div>
        {canEditCalendar && (
          <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetEventForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="add-event-btn" onClick={openAddEvent}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2"><rect width="18" height="18" x="3" y="4" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="12" y1="14" x2="12" y2="18"/><line x1="10" y1="16" x2="14" y2="16"/></svg>
                Add Event
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{editingEventId ? "Edit Calendar Event" : "Add Calendar Event"}</DialogTitle></DialogHeader>
              <form onSubmit={handleSaveEvent} className="space-y-4 pt-4">
                <div className="space-y-2"><Label>Title *</Label><Input value={newEvent.title} onChange={(e) => setNewEvent(p => ({ ...p, title: e.target.value }))} required data-testid="event-title-input" /></div>
                <div className="space-y-3 p-4 rounded-lg border bg-muted/30" data-testid="whos-going-section">
                  <div className="flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#00305E]"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                    <Label className="font-medium">Who&apos;s going?</Label>
                  </div>
                  <div className="flex items-center justify-between mb-1">
                    <button type="button" onClick={handleSelectAllStaff} className="text-sm text-[#00305E] hover:underline font-medium" data-testid="whos-going-select-all">{selectAllStaff ? "UNSELECT ALL" : "SELECT ALL"}</button>
                    <span className="text-xs text-muted-foreground">
                      {newEvent.notify_staff?.length || 0} selected{selectAllStaff && " · shows as orange"}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 max-h-[180px] overflow-y-auto">
                    {staff.map(s => (
                      <label key={s.id} className="flex items-center gap-2 p-2 rounded hover:bg-muted cursor-pointer">
                        <Checkbox checked={newEvent.notify_staff?.includes(s.id)} onCheckedChange={() => handleStaffNotifyToggle(s.id)} data-testid={`whos-going-${s.id}`} />
                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: getStaffColor(s) }}></span>
                        <span className="text-sm truncate">{s.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Start Date *</Label><Input type="date" value={newEvent.date} onChange={(e) => setNewEvent(p => ({ ...p, date: e.target.value }))} required /></div>
                  <div className="space-y-2"><Label>End Date (optional)</Label><Input type="date" value={newEvent.end_date} onChange={(e) => setNewEvent(p => ({ ...p, end_date: e.target.value }))} /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Start Time (optional)</Label><Input type="time" value={newEvent.start_time} onChange={(e) => setNewEvent(p => ({ ...p, start_time: e.target.value }))} data-testid="event-start-time" /></div>
                  <div className="space-y-2"><Label>End Time (optional)</Label><Input type="time" value={newEvent.end_time} onChange={(e) => setNewEvent(p => ({ ...p, end_time: e.target.value }))} data-testid="event-end-time" /></div>
                </div>
                <div className="space-y-2">
                  <Label>Event Type</Label>
                  <Select value={newEvent.type} onValueChange={(v) => setNewEvent(p => ({ ...p, type: v }))}>
                    <SelectTrigger data-testid="event-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="holiday">Holiday/Break</SelectItem><SelectItem value="snow_day">Snow Day</SelectItem>
                      <SelectItem value="meeting">Meeting</SelectItem><SelectItem value="event">Event</SelectItem>
                      <SelectItem value="themed_week">Themed Week</SelectItem><SelectItem value="tour">Tour</SelectItem><SelectItem value="staff_pto">Staff PTO</SelectItem><SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-6">
                  <div className="flex items-center space-x-2"><Checkbox id="half-day" checked={newEvent.is_half_day} onCheckedChange={(c) => setNewEvent(p => ({ ...p, is_half_day: c }))} /><Label htmlFor="half-day" className="cursor-pointer">Half Day</Label></div>
                  <div className="flex items-center space-x-2"><Checkbox id="public" checked={newEvent.is_public} onCheckedChange={(c) => setNewEvent(p => ({ ...p, is_public: c }))} /><Label htmlFor="public" className="cursor-pointer">Public</Label></div>
                </div>
                <div className="space-y-2"><Label>Notes</Label><textarea value={newEvent.notes || ""} onChange={(e) => setNewEvent(p => ({ ...p, notes: e.target.value }))} className="w-full min-h-[80px] p-3 text-sm border rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-[#00305E]/20" placeholder="Add any additional notes..." maxLength={2000} /><p className="text-xs text-muted-foreground text-right">{(newEvent.notes || "").length} / 2000</p></div>
                <div className="flex flex-col items-end gap-2 pt-4">
                  {savingEvent && (
                    <p className="text-sm text-[#00305E] font-medium w-full text-center" data-testid="event-saving-message">
                      Great things take time. Please wait for your event to save.
                    </p>
                  )}
                  <div className="flex justify-end gap-3">
                    <Button type="button" variant="outline" disabled={savingEvent} onClick={() => { setDialogOpen(false); resetEventForm(); }}>Cancel</Button>
                    <Button
                      type="submit"
                      disabled={savingEvent}
                      className={`bg-[#00305E] hover:bg-[#00305E]/90 ${savingEvent ? "opacity-50 cursor-not-allowed pointer-events-none" : ""}`}
                      data-testid="create-event-btn"
                    >
                      {savingEvent ? "Saving…" : (editingEventId ? "Save Changes" : "Create Event")}
                    </Button>
                  </div>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="master-schedule" data-testid="tab-master-schedule">Master Schedule</TabsTrigger>
          <TabsTrigger value="calendar" data-testid="tab-calendar">Meetings and Events</TabsTrigger>
          <TabsTrigger value="year-calendar" data-testid="tab-year-calendar">School Calendar Year</TabsTrigger>
        </TabsList>

        <TabsContent value="calendar">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3" data-testid="calendar-loading">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#00305E]"></div>
              Loading events…
            </div>
          )}
          <SchoolCalendarTab selectedDate={selectedDate} setSelectedDate={setSelectedDate}
            viewMode={viewMode} setViewMode={setViewMode} events={events} roster={roster}
            selectedDateEvents={selectedDateEvents} getEventsForDate={getEventsForDate}
            getEventTypeColor={getEventTypeColor} handleDeleteEvent={handleDeleteEvent} onEditEvent={openEditEvent} onAddEventForDate={openAddEventForDate} isAdmin={() => canEditCalendar}
            staff={staff} staffById={staffById} activeStaffIds={activeStaffIds}
            staffFilter={staffFilter} setStaffFilter={setStaffFilter} currentUserId={user?.id} searchEvents={searchEvents} />
        </TabsContent>

        <TabsContent value="year-calendar">
          <SchoolYearCalendarTab schoolCalendar={schoolCalendar} generateSchoolYearMonths={generateSchoolYearMonths}
            countSchoolDays={countSchoolDays} getSpecialDateInfo={getSpecialDateInfo}
            handleDeleteSpecialDate={handleDeleteSpecialDate}
            handleDeleteKeyDate={handleDeleteKeyDate}
            calendarDialogOpen={calendarDialogOpen} setCalendarDialogOpen={setCalendarDialogOpen}
            addDateDialogOpen={addDateDialogOpen} setAddDateDialogOpen={setAddDateDialogOpen}
            calendarFormData={calendarFormData} setCalendarFormData={setCalendarFormData}
            newSpecialDate={newSpecialDate} setNewSpecialDate={setNewSpecialDate}
            handleCreateCalendar={handleCreateCalendar} handleAddSpecialDate={handleAddSpecialDate}
            handlePrintCalendar={handlePrintCalendar} onRefresh={fetchEvents}
            allCalendars={allCalendars} selectedCalendarYear={selectedCalendarYear}
            onYearChange={(year) => {
              const cal = allCalendars.find(c => c.academic_year === year);
              if (cal) {
                setSelectedCalendarYear(year);
                setSchoolCalendar(cal);
                setCalendarFormData({ academic_year: cal.academic_year, school_name: cal.school_name, first_day: cal.first_day, last_day: cal.last_day });
              }
            }} />
          <div className="mt-6">
            <SchoolClosuresSection academicYear={selectedCalendarYear || schoolCalendar?.academic_year} />
          </div>
        </TabsContent>

        <TabsContent value="master-schedule">
          <MasterScheduleTab initialTodayView={openTodayView} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
